import { useCallback, useMemo, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { colors } from "@/components/colors"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { usePdfBuilderStore, selectCurrentPageConfig } from "@/stores/pdfBuilder"
import {
  CANVAS_TOP_PX,
  GRID_ROW_PX,
  fillToPageBottomRows,
  groupedTopPadPx,
  planPageBlocks,
} from "./engine/pageBlocks"
import { PAPER_DIMENSIONS } from "@/types/pdfBuilder"
import { ACTIVITY_SESSION_TYPE, REPORT_TYPE } from "@/app/report/common/constants"
import { Sort, TableHeaderColumn } from "@/app/report/common/constructor"
import type { StatsSettings, TableRow } from "@/app/report/common/type"
import {
  getBuilderTableSections,
  resolveBuilderTableSection,
} from "@/app/report/common/sections/customTable"
import {
  buildCustomTableSummaryRows,
  createCompareByGroupKey,
  mapTableBodyToRows,
  resolveSummaryRowKinds,
  sortCustomTableRows,
  useCustomTableData,
} from "@/app/report/hooks/useCustomTableData"
import { usePositionRank, usePositionGroupRank } from "@/hooks/usePositionRank"
import PrintableTableChunk from "@/app/report/components/section/pdf/PrintableTableChunk"
import { createGroupId } from "@/app/report/pdf/builder/utils/paletteItems"
import type { ChartComponentProps } from "./types"

import "./builderCharts.scss"

/**
 * PDF 빌더 Table — 리포트 화면의 Custom Table 섹션과 같은 것 (일감 #75, 3단계).
 *
 * 컬럼·그룹·계산 방식·정렬·요약 행은 Report Filters 에서 고른 리포트 템플릿의 Custom Table 설정을 그대로 따르고,
 * 데이터는 리포트 화면·리포트 PDF 와 같은 훅(useCustomTableData: 모드별 그룹 집계)과 같은 빌더
 * (정렬·요약 행)로 만들며, 표 한 장은 리포트 PDF 와 같은 컴포넌트(PrintableTableChunk)로 그린다.
 * 이 파일은 빌더 고유 사정 — 캔버스 폭·페이지 슬롯에 맞춘 열/행 분할, 칸 높이 자동 조정, 빈 상태 — 만 다룬다.
 */

// 열 폭은 리포트 PDF(printableTable.scss, scale 1)의 규칙과 같다 — 이름 104, 등번호 45, 포지션·인원 55, 그 외 63
const NAME_COLUMN_PX = 104
const NUMBER_COLUMN_PX = 45
const SHORT_COLUMN_PX = 55
const DEFAULT_COLUMN_PX = 63
// 지표 열은 한 장에 세로 최대 10개·가로 최대 12개까지 넣고, 안 들어가면 열 폭을 이 값까지 줄인다 (2026-09-28 요청).
// 리포트 PDF 는 1000px 기준 배치를 0.79 배로 줄여 63px 열이 50px 로 보인다 — 빌더도 같은 열 수가 나오게 맞춘다
const MIN_COLUMN_PX = 40
export const MAX_METRICS_PER_PAGE_PORTRAIT = 10
// 가로 캔버스 폭 1083px 에서 고정 열(등번호 45·이름 104·포지션 55)을 빼면 지표 14열이 약 62px 로 들어간다 (2026-09-28)
export const MAX_METRICS_PER_PAGE_LANDSCAPE = 14
// 분할은 항상 수동(장당 행 수·지표 수) — Split Table off/auto 는 없앴다 (2026-09-28 요청). 행 수 기본값은
// 용지 방향별 기본 장당 행 수 — 세로 25, 가로 16 (2026-10-01 결정)
export const DEFAULT_ROWS_PER_PAGE_PORTRAIT = 25
export const DEFAULT_ROWS_PER_PAGE_LANDSCAPE = 16
export const defaultRowsPerPage = (isLandscape: boolean) =>
  isLandscape ? DEFAULT_ROWS_PER_PAGE_LANDSCAPE : DEFAULT_ROWS_PER_PAGE_PORTRAIT
// 장당 행 수 상한 — 세로 33(한 장 높이에서 여백 20·장 번호 16·머리글 52 를 뺀 값 ÷ 행 30px), 가로 18(794px: 540+52+60+여백 < 794)
export const MAX_ROWS_PER_PAGE_PORTRAIT = 33
export const MAX_ROWS_PER_PAGE_LANDSCAPE = 18
export const maxRowsPerPage = (isLandscape: boolean) =>
  isLandscape ? MAX_ROWS_PER_PAGE_LANDSCAPE : MAX_ROWS_PER_PAGE_PORTRAIT
// 표 묶음에서 새 장 아이템의 시작 높이(격자 행). 내용에 맞춰 곧바로 자동 조정된다
const NEW_PAGE_ITEM_ROWS = 12
const SHORT_COLUMN_KEYS = [
  "position",
  "position1",
  "position2",
  "playerCount",
  "activityCount",
  "sessionCount",
  "matchDay",
  "matchDayType",
]

// 첫 덩어리 위에는 CanvasItem 제목이 있고(제목 30 + 위 여백 8 + 테두리 2 = 40px),
// 둘째 덩어리부터는 없다. 그만큼 위를 띄워 덩어리마다 차지하는 높이를 같게 맞춘다 —
// 그래야 페이지 경계선과 덩어리 경계가 어긋나지 않는다. 내보낼 때는 제목이 숨겨지므로 0.
const TITLE_BLOCK_PX = 40
// auto(한 표로 길게)에서 페이지 단위로 행을 나눌 때 쓰는 높이 — 리포트 PDF 표의 행 30px, 머리글 기본 52px,
// 덩어리 번호 24px, 페이지 경계 앞뒤 여백 24px (Action Map 의 연속 모드와 같은 값)
const TABLE_ROW_PX = 30
const DEFAULT_HEADER_PX = 52
const CHUNK_LABEL_PX = 24
const PAGE_MARGIN_PX = 24

const columnWidthOf = (key: string): number => {
  if (/Name$/.test(key) || key === "nickname") {
    return NAME_COLUMN_PX
  }
  if (key === "uniformNumber") {
    return NUMBER_COLUMN_PX
  }
  if (SHORT_COLUMN_KEYS.includes(key)) {
    return SHORT_COLUMN_PX
  }

  return DEFAULT_COLUMN_PX
}

// 템플릿에 Custom Table 이 없을 때 훅에 넘기는 빈 설정 — groupKey1 이 없어 조회가 일어나지 않는다
const EMPTY_SETTINGS: StatsSettings = {
  title: "",
  fixedColumns: [],
  calcType: "total",
  metrics: [],
}

export default function BuilderDataTable({ item, width }: ChartComponentProps) {
  const { t } = useTranslation()
  const {
    reportTemplate,
    reportMode,
    resolveItemPlayerIds,
    hasActivitySelection,
    reportKind,
    trendActivityIds,
  } = usePdfDataContext()
  // 트렌드는 기간 안의 액티비티 전부를 누적 집계한다 — 세션 모드는 쓰지 않는다 (2026-10-02 요청)
  const isTrend = reportKind === "trend"

  const currentPageId = usePdfBuilderStore((s) => s.currentPageId)
  const updateItemLayout = usePdfBuilderStore((s) => s.updateItemLayout)
  const updateItem = usePdfBuilderStore((s) => s.updateItem)

  // ── 표 묶음: 출력 장마다 아이템 하나 (2026-09-28 요청). 같은 groupId 아이템을 위→아래 순으로 세어
  //    이 아이템이 몇 번째 장(rank)인지 정한다. 첫 아이템(rank 0)이 장 수에 맞춰 아이템을 늘리고 줄인다.
  const groupId = item.options.groupId
  const groupKey = usePdfBuilderStore((s) => {
    if (!groupId) {
      return ""
    }
    const page = s.pages.find((p) => p.id === s.currentPageId)

    return (page?.items ?? [])
      .filter((candidate) => candidate.options.groupId === groupId)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((candidate) => `${candidate.id}:${candidate.y}`)
      .join(",")
  })
  const groupMembers = useMemo(
    () =>
      groupKey
        ? groupKey.split(",").map((entry) => {
            const [id, y] = entry.split(":")

            return { id, y: Number(y) }
          })
        : [],
    [groupKey],
  )
  // Table Scale — auto(기본): 아이템 하나에 한 표로 길게(페이지 경계에서 행이 잘리지 않게 여백),
  // manual: 장당 행·지표 수로 출력 장마다 아이템 하나(묶음). 묶음 동작은 manual 에서만 (2026-09-29 요청)
  const isManual = (item.options.tableScale ?? "auto") === "manual"
  const memberRank = groupMembers.length
    ? Math.max(
        0,
        groupMembers.findIndex((member) => member.id === item.id),
      )
    : 0
  const isGrouped = isManual && groupMembers.length > 0
  const groupRank = isGrouped ? memberRank : 0
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  const filterSessionIds = usePdfBuilderStore((s) => s.filterSessionIds)
  const pageConfig = usePdfBuilderStore(selectCurrentPageConfig)
  const paperSize = pageConfig?.size === "letter" ? "letter" : "a4"
  const pageDims = PAPER_DIMENSIONS[paperSize]
  const isLandscape = pageConfig?.orientation === "landscape"
  const pageHeight = isLandscape ? pageDims.width : pageDims.height

  const isExporting = usePdfBuilderStore((s) => s.isExporting)
  const tableRef = useRef<HTMLDivElement>(null)

  // manual 로 바꾸면 묶음 id 를 만든다 — 묶음 id 가 없던(옛) 아이템도 장마다 아이템 하나로 나뉘고,
  // 속성 패널이 이 id 로 설정을 묶음 전체에 같이 적용한다
  useEffect(() => {
    if (isManual && !groupId && currentPageId && !isExporting) {
      updateItem(currentPageId, item.id, {
        options: { ...item.options, groupId: createGroupId("tbl") },
      })
    }
  }, [isManual, groupId, currentPageId, isExporting, updateItem, item.id, item.options])

  // ChartRenderer 가 넘겨주는 실제 컨테이너 폭. 0 이면 A4 세로 기준으로 시작한다
  const availableWidth = width > 0 ? width : 754
  const maxMetricsPerPage = isLandscape
    ? MAX_METRICS_PER_PAGE_LANDSCAPE
    : MAX_METRICS_PER_PAGE_PORTRAIT
  // 장당 행 수(0 = 행 분할 없음)·지표 수 — 지표 수는 방향별 상한을 넘지 않는다
  const manualRows = Math.min(
    item.options.rowsPerPage ?? defaultRowsPerPage(isLandscape),
    maxRowsPerPage(isLandscape),
  )
  const manualMetrics = Math.max(
    1,
    Math.min(item.options.metricsPerPage ?? maxMetricsPerPage, maxMetricsPerPage),
  )
  const sectionGapMode = item.options.sectionGapMode ?? "auto"
  // manual 일 때만 공백을 쓴다. auto 는 덩어리 높이를 페이지 한 장으로 고정하므로 공백이 0 이다.
  const sectionGap = sectionGapMode === "manual" ? (item.options.sectionGap ?? 16) : 0

  // ── 설정: 리포트 템플릿의 Custom Table 섹션 (아이템이 가리키는 것, 없으면 첫 표) ──
  const templateSections = useMemo(() => getBuilderTableSections(reportTemplate), [reportTemplate])
  const templateSection = useMemo(
    () => resolveBuilderTableSection(templateSections, item.dataSource.sectionIndex),
    [templateSections, item.dataSource.sectionIndex],
  )
  // 지표만 아이템이 덮어쓴다 — 처음 추가될 때 템플릿 지표를 가져오고(아래 effect), 그 뒤 속성 패널에서 더하고 뺀다.
  // 컬럼 구성·그룹·계산 방식·정렬·요약 행은 템플릿 그대로 (결정 2026-09-23)
  const itemMetrics = item.dataSource.metrics
  const settings = useMemo<StatsSettings>(() => {
    if (!templateSection) {
      return EMPTY_SETTINGS
    }
    if (!itemMetrics) {
      return templateSection.settings
    }

    return { ...templateSection.settings, metrics: itemMetrics }
  }, [templateSection, itemMetrics])

  // 처음 추가된 아이템(metrics 미정)은 템플릿의 Custom Table 지표를 선택된 상태로 가져온다.
  // 저장된 템플릿의 기존 아이템은 자기 지표를 그대로 둔다.
  useEffect(() => {
    if (itemMetrics !== undefined || !templateSection || !currentPageId) {
      return
    }
    updateItem(currentPageId, item.id, {
      dataSource: { ...item.dataSource, metrics: [...templateSection.settings.metrics] },
    })
  }, [itemMetrics, templateSection, currentPageId, item.id, item.dataSource, updateItem])

  // ── 데이터: 리포트 화면·PDF 와 같은 훅. 아이템(Data Source)의 선수 지정은 Report Filters 범위 안에서 ──
  const itemPlayerIds = useMemo(
    () => resolveItemPlayerIds(item.dataSource.playerIds),
    [resolveItemPlayerIds, item.dataSource.playerIds],
  )
  const activityIds = useMemo(
    () => (isTrend ? trendActivityIds : filterActivityId ? [filterActivityId] : []),
    [isTrend, trendActivityIds, filterActivityId],
  )
  const isSessionMode = !isTrend && reportMode === ACTIVITY_SESSION_TYPE.SESSION
  const {
    tableHeader,
    tableBody,
    rowSources,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    displayColumnCount,
    isLoading,
  } = useCustomTableData(settings, {
    reportType: isTrend ? REPORT_TYPE.TREND : REPORT_TYPE.DAILY,
    sessionMode: isSessionMode,
    sessionIds: isSessionMode ? filterSessionIds : undefined,
    activityIds,
    playerIds: itemPlayerIds,
  })

  // ── 정렬·요약 행: 리포트 PDF(PrintableCustomTableSection)와 같은 빌더 ──
  const positionRank = usePositionRank()
  const positionGroupRank = usePositionGroupRank()
  const compareByGroupKey = useMemo(
    () => createCompareByGroupKey(positionRank, positionGroupRank),
    [positionRank, positionGroupRank],
  )
  // 화면의 임시 정렬(store)은 리포트 페이지 안에서만 유효하다 — 빌더는 아이템 옵션(머리글 클릭) > 템플릿 정렬 (#185)
  const templateSort = useMemo(
    () =>
      item.options.sortKey
        ? { key: item.options.sortKey, order: item.options.sortOrder ?? ("asc" as const) }
        : settings.sortSetting,
    [item.options.sortKey, item.options.sortOrder, settings.sortSetting],
  )
  // 머리글 클릭: 같은 컬럼이면 방향 반전, 아니면 오름차순. 묶음(장)의 모든 아이템에 같이 적용
  const handleHeaderClick = useCallback(
    (key: string) => {
      if (!currentPageId) {
        return
      }
      const state = usePdfBuilderStore.getState()
      const page = state.pages.find((p) => p.id === currentPageId)
      if (!page) {
        return
      }
      const current = page.items.find((candidate) => candidate.id === item.id)
      const nextOrder: "asc" | "desc" =
        current?.options.sortKey === key && (current.options.sortOrder ?? "asc") === "asc"
          ? "desc"
          : "asc"
      const groupId = current?.options.groupId
      page.items
        .filter(
          (candidate) =>
            candidate.id === item.id || (!!groupId && candidate.options.groupId === groupId),
        )
        .forEach((candidate) =>
          state.updateItem(currentPageId, candidate.id, {
            options: { ...candidate.options, sortKey: key, sortOrder: nextOrder },
          }),
        )
    },
    [currentPageId, item.id],
  )
  const visibleRows = useMemo(() => {
    if (!tableBody.length) {
      return []
    }

    return sortCustomTableRows(
      mapTableBodyToRows(tableBody, tableHeader),
      templateSort,
      settings.groupKey1,
      settings.groupKey2,
      compareByGroupKey,
    )
  }, [
    tableBody,
    tableHeader,
    templateSort,
    settings.groupKey1,
    settings.groupKey2,
    compareByGroupKey,
  ])

  // 요약 행(Total·Average)은 아이템 옵션이 정한다 — 기본 둘 다 켬 (2026-09-30 요청). 템플릿의 summaryRows 는 보지 않는다:
  // 진짜 Custom Table 에는 그 필드가 없어(없으면 미표시) 따르면 빌더에서 요약 행을 켤 방법이 없다
  const showTotalRow = item.options.showTotalRow ?? true
  const showAverageRow = item.options.showAverageRow ?? true
  const summaryRowKinds = useMemo(
    () => resolveSummaryRowKinds({ total: showTotalRow, average: showAverageRow }),
    [showTotalRow, showAverageRow],
  )
  const summaryRows = useMemo(() => {
    if (!visibleRows.length || !summaryRowKinds.length) {
      return []
    }

    return buildCustomTableSummaryRows({
      tableHeader,
      displayColumnCount,
      rowSources: visibleRows
        .map((row) => rowSources[row._rowIndex as number])
        .filter((source): source is (typeof rowSources)[number] => !!source),
      metricMap,
      selectedUnitList,
      calculatedMetrics,
      t,
      kinds: summaryRowKinds,
    })
  }, [
    visibleRows,
    rowSources,
    tableHeader,
    displayColumnCount,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    summaryRowKinds,
    t,
  ])

  const bodyRows = useMemo<TableRow[]>(
    () => visibleRows.map((row) => tableBody[row._rowIndex as number]),
    [visibleRows, tableBody],
  )
  const headerColumns = useMemo(
    () => tableHeader.map((header) => new TableHeaderColumn(header)),
    [tableHeader],
  )
  const sort = useMemo(
    () => (templateSort ? new Sort(templateSort.key, templateSort.order.toUpperCase()) : undefined),
    [templateSort],
  )

  // 머리글 높이를 덩어리마다 다르게 두면(지표 이름 길이에 따라 줄 수가 달라진다) 표 높이가 어긋나
  // 페이지 경계와 맞출 수 없다. 가장 높은 머리글을 기준으로 모두 통일한다.
  // 높이는 "글자가 실제로 몇 줄인지"로 재므로, 높이를 고정한 뒤에도 값이 변하지 않는다(되먹임 없음).
  const [headerHeight, setHeaderHeight] = useState<number | null>(null)

  // ── 분할: 지표 열 묶음 × 행 묶음 = 덩어리 하나 (출력 한 장). 항상 수동 설정을 쓴다 ──
  const isSplitActive = true
  const firstPageRows = manualRows
  const overflowRows = manualRows
  const metricsPerPage = manualMetrics
  // auto 의 페이지 나누기 기준 — 표 첫 덩어리의 캔버스 위치(아이템 위 + 제목 영역)와 요약 행 수
  const summaryRowCount = summaryRows.length
  const autoTopPx = item.y * 40 + 20 + TITLE_BLOCK_PX
  // 제목 + 머리글 + 요약 행 + 첫 행이 페이지 끝에 안 들어가면 제목째 다음 페이지로 — 그만큼을 CanvasItem 위 여백(headGap)으로
  // 두고 표는 그 뒤에서 시작한다 (2026-10-01 보고: 제목만 페이지 끝에 남았다). 묶음(Manual)은 장마다 따로 두는 기존 규칙
  const autoHeadGapPx = useMemo(() => {
    if (isManual || headerColumns.length === 0 || bodyRows.length === 0 || headerHeight === null) {
      return 0
    }
    const firstBlockPx =
      TITLE_BLOCK_PX + headerHeight + summaryRowCount * TABLE_ROW_PX + CHUNK_LABEL_PX + TABLE_ROW_PX
    const { padTops } = planPageBlocks([firstBlockPx], autoTopPx - TITLE_BLOCK_PX, pageHeight)

    return padTops[0] ?? 0
  }, [
    isManual,
    headerColumns.length,
    bodyRows.length,
    headerHeight,
    summaryRowCount,
    autoTopPx,
    pageHeight,
  ])
  const setHeadGap = usePdfBuilderStore((s) => s.setHeadGap)
  useEffect(() => {
    setHeadGap(item.id, isGrouped && groupRank > 0 ? 0 : autoHeadGapPx)
  }, [setHeadGap, item.id, isGrouped, groupRank, autoHeadGapPx])

  const tableChunks = useMemo(() => {
    if (headerColumns.length === 0 || bodyRows.length === 0) {
      return []
    }

    const leadingColumns = headerColumns.slice(0, displayColumnCount)
    const metricColumns = headerColumns.slice(displayColumnCount)
    const leadingWidth = leadingColumns.reduce((sum, column) => sum + columnWidthOf(column.key), 0)

    // 한 장에 들어갈 수 있는 지표 열 수 — 방향별 상한(세로 10·가로 14)과 최소 열 폭(40px)으로 정한다.
    // 사용자가 더 많이 지정해도 이 수를 넘기지 않는다 — 넘기면 오른쪽 열이 잘린다.
    const maxMetricsPerChunk = Math.max(
      1,
      Math.min(maxMetricsPerPage, Math.floor((availableWidth - leadingWidth) / MIN_COLUMN_PX)),
    )
    const effectiveMetricsPerPage =
      metricsPerPage > 0 ? Math.min(metricsPerPage, maxMetricsPerChunk) : maxMetricsPerChunk

    const metricGroups: TableHeaderColumn[][] = []
    if (metricColumns.length === 0) {
      metricGroups.push([])
    } else if (effectiveMetricsPerPage < metricColumns.length) {
      for (let i = 0; i < metricColumns.length; i += effectiveMetricsPerPage) {
        metricGroups.push(metricColumns.slice(i, i + effectiveMetricsPerPage))
      }
    } else {
      metricGroups.push(metricColumns)
    }

    // 행 묶음 — 첫 장은 표가 페이지 중간에서 시작하면 더 적게 담는다 (manual). auto 는 아래에서 페이지 단위로 다시 나눈다
    const rowGroups: TableRow[][] = []
    if (isManual && firstPageRows > 0 && firstPageRows < bodyRows.length) {
      rowGroups.push(bodyRows.slice(0, firstPageRows))
      const remaining = bodyRows.slice(firstPageRows)
      const perPage = overflowRows > 0 ? overflowRows : remaining.length
      for (let i = 0; i < remaining.length; i += perPage) {
        rowGroups.push(remaining.slice(i, i + perPage))
      }
    } else {
      rowGroups.push(bodyRows)
    }

    // 지표 열 폭은 "가득 찬 묶음"(첫 묶음) 기준 — 마지막 묶음이 열이 적어도 폭을 늘리지 않아
    // 모든 덩어리의 열 너비가 같고 페이지마다 표가 같은 모양으로 나온다 (리포트 PDF WEB-2276 과 같은 규칙).
    // 열 수가 적으면 그만큼 넓혀 가로를 꽉 채운다 (63px 상한 없음 — 2026-09-28 요청)
    const fullGroupSize = Math.max(1, metricGroups[0]?.length ?? 1)
    const metricColumnWidth = Math.max(
      MIN_COLUMN_PX,
      Math.floor((availableWidth - leadingWidth) / fullGroupSize),
    )

    const chunks: {
      columns: TableHeaderColumn[]
      columnIndexes: number[]
      columnWidths: Record<string, number>
      tableWidth: number
      rows: TableRow[]
      label: string
      padTop: number
      /** auto: 이 조각의 상자가 차지할 높이(페이지 끝까지). 계산값으로 고정해 다음 조각이 정확히 다음 페이지에서 시작한다 */
      slotPx: number
    }[] = []

    metricGroups.forEach((metricGroup, mIdx) => {
      const columns = [...leadingColumns, ...metricGroup]
      const columnIndexes = columns.map((column) => headerColumns.indexOf(column))
      const columnWidths: Record<string, number> = {}
      leadingColumns.forEach((column) => (columnWidths[column.key] = columnWidthOf(column.key)))
      metricGroup.forEach((column) => (columnWidths[column.key] = metricColumnWidth))
      const tableWidth = leadingWidth + metricGroup.length * metricColumnWidth

      rowGroups.forEach((rowGroup, rIdx) => {
        const label =
          metricGroups.length > 1 || rowGroups.length > 1
            ? `${mIdx * rowGroups.length + rIdx + 1} / ${metricGroups.length * rowGroups.length}`
            : ""
        chunks.push({
          columns,
          columnIndexes,
          columnWidths,
          tableWidth,
          rows: rowGroup,
          label,
          padTop: 0,
          slotPx: 0,
        })
      })
    })

    if (isManual) {
      return chunks
    }

    // auto: 한 표로 길게 잇되 페이지 경계에 걸리는 행은 다음 페이지 첫머리(여백 뒤)로 넘긴다 — padTop 이 그 빈 공간.
    // 지표 묶음이 여럿이면 순서대로 이어 붙인다 (Action Map 의 연속 모드와 같은 규칙, 2026-09-29 요청)
    const summaryPx = summaryRowCount * TABLE_ROW_PX
    const headerPx = headerHeight ?? DEFAULT_HEADER_PX
    const paged: typeof chunks = []
    // 제목째 밀린 만큼(headGap) 뒤에서 표가 시작한다
    let cursor = autoTopPx + autoHeadGapPx
    // 빈 공간(pad)은 조각을 넘어 이어진다 — 지표 묶음이 바뀔 때도 앞 조각이 남긴 "다음 페이지까지" 공간을 그대로 쓴다
    let pad = 0
    chunks.forEach((chunk) => {
      const blockPx = headerPx + summaryPx + (chunks.length > 1 ? CHUNK_LABEL_PX : 0)
      let start = 0
      let guard = 0
      while (start < chunk.rows.length && guard < 200) {
        guard += 1
        const pageIndex = Math.floor(cursor / pageHeight)
        const intoPage = cursor - pageIndex * pageHeight
        if (intoPage < PAGE_MARGIN_PX) {
          pad += PAGE_MARGIN_PX - intoPage
          cursor += PAGE_MARGIN_PX - intoPage
          continue
        }
        const pageBottom = (pageIndex + 1) * pageHeight - PAGE_MARGIN_PX
        const fit = Math.floor((pageBottom - cursor - blockPx) / TABLE_ROW_PX)
        if (fit < 1) {
          const nextTop = (pageIndex + 1) * pageHeight + PAGE_MARGIN_PX
          pad += nextTop - cursor
          cursor = nextTop
          continue
        }
        const count = Math.min(fit, chunk.rows.length - start)
        // 표 하나가 페이지 한 장 — 조각 상자를 페이지 끝까지(slotPx) 고정해 이어지는 행이든 다음 지표 묶음이든
        // 다음 조각이 정확히 다음 페이지 첫머리(여백 뒤)에서 시작한다. 재서 맞추지 않고 계산값으로 고정하므로
        // 조각마다 몇 px 씩 어긋나 쌓이지 않는다. 아이템을 끌어 옮기면 autoTopPx 가 바뀌어 다시 계산된다 (2026-09-29 요청)
        paged.push({
          ...chunk,
          rows: chunk.rows.slice(start, start + count),
          padTop: pad,
          slotPx: (pageIndex + 1) * pageHeight - (cursor - pad),
          label: "",
        })
        start += count
        pad = PAGE_MARGIN_PX
        cursor = (pageIndex + 1) * pageHeight + PAGE_MARGIN_PX
      }
    })

    return paged.map((chunk, index) => ({
      ...chunk,
      label: paged.length > 1 ? `${index + 1} / ${paged.length}` : "",
    }))
  }, [
    headerColumns,
    bodyRows,
    displayColumnCount,
    metricsPerPage,
    firstPageRows,
    overflowRows,
    availableWidth,
    maxMetricsPerPage,
    isManual,
    summaryRowCount,
    headerHeight,
    autoTopPx,
    autoHeadGapPx,
    pageHeight,
  ])

  // 묶음이면 이 아이템은 자기 장(rank)만 그린다. 장이 없어진 아이템(rank ≥ 장 수)은 첫 아이템이 곧 지운다
  const renderedChunks = useMemo(
    () => (isGrouped ? tableChunks.slice(groupRank, groupRank + 1) : tableChunks),
    [isGrouped, tableChunks, groupRank],
  )

  // 표 묶음의 장이 페이지 경계 바로 앞(격자 반올림으로 남은 자투리)에서 시작하면 내용을 다음 페이지 첫머리로 민다 —
  // 그대로 두면 내보낼 때 장의 머리글이 페이지 경계에 걸려 잘린다. 자투리가 머리글+몇 행이 들어갈 만큼이면 그대로 쓴다
  const groupedTopPad = useMemo(
    () => (isGrouped ? groupedTopPadPx(item.y * GRID_ROW_PX + CANVAS_TOP_PX, pageHeight) : 0),
    [isGrouped, item.y, pageHeight],
  )

  // 머리글 높이 측정 — 그릴 덩어리(renderedChunks)가 정해진 뒤에 잰다. 표 묶음의 새 장은 장 번호가 잡히기 전에
  // 먼저 마운트되므로, 덩어리 없이 잰 0 으로 끝나면 자동 높이가 영영 안 돌았다 (2026-09-28 재현)
  useEffect(() => {
    const el = tableRef.current
    if (!el) {
      return
    }

    // 머리글 안쪽 컬럼 div(.table-header-column)는 내용 높이만 갖는다 — tr 높이를 고정한 뒤에도 값이 같아 되먹임이 없다
    // 캔버스 줌(CSS scale)을 빼고 레이아웃 px 로 잰다 (2026-09-30 보고: 확대 상태에서 추가하면 칸이 잘림)
    const zoom = el.offsetWidth > 0 ? el.getBoundingClientRect().width / el.offsetWidth : 1
    let tallest = 0
    el.querySelectorAll<HTMLElement>("thead th").forEach((th) => {
      const content = th.firstElementChild as HTMLElement | null
      const contentHeight = (content?.getBoundingClientRect().height ?? 0) / (zoom || 1)
      const style = getComputedStyle(th)
      const vertical =
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        (parseFloat(style.borderBottomWidth) || 0)
      tallest = Math.max(tallest, Math.ceil(contentHeight + vertical))
    })

    if (tallest <= 0) {
      return
    }
    setHeaderHeight((prev) => (prev !== null && Math.abs(prev - tallest) <= 1 ? prev : tallest))
  }, [
    bodyRows.length,
    headerColumns.length,
    displayColumnCount,
    availableWidth,
    isExporting,
    renderedChunks,
  ])

  // 첫 장 아이템이 묶음의 아이템 수를 장 수에 맞춘다 — 모자라면 마지막 장 바로 아래에 새 아이템(같은 열·폭, 같은 설정)을
  // 붙이고, 남으면 뒤에서부터 지운다. 표 장들 사이에 다른 컴포넌트가 끼지 않도록, 그 자리에 있던 다른 아이템들은
  // 새 장들 높이만큼 아래로 먼저 밀어 둔다 (2026-09-28 요청 — 이후 사용자가 직접 끌어 옮기는 것은 막지 않는다).
  // 내보내는 중에는 배치를 바꾸지 않는다.
  useEffect(() => {
    if (
      !groupId ||
      groupMembers.length === 0 ||
      memberRank !== 0 ||
      isExporting ||
      !currentPageId
    ) {
      return
    }
    if (tableChunks.length === 0) {
      return
    }
    const state = usePdfBuilderStore.getState()
    const page = state.pages.find((p) => p.id === currentPageId)
    if (!page) {
      return
    }
    const members = page.items
      .filter((candidate) => candidate.options.groupId === groupId)
      .sort((a, b) => a.y - b.y || a.x - b.x)
    // auto(한 표로 길게)로 돌아가면 아이템 하나만 남긴다
    const needed = isManual ? tableChunks.length : 1
    if (members.length < needed) {
      const selectedBefore = state.selectedItemId
      const last = members[members.length - 1] ?? item
      const insertY = last.y + last.h
      const shift = (needed - members.length) * NEW_PAGE_ITEM_ROWS
      page.items
        .filter((other) => other.options.groupId !== groupId && other.y >= insertY)
        .forEach((other) => state.updateItemLayout(currentPageId, other.id, { y: other.y + shift }))
      let nextY = insertY
      for (let index = members.length; index < needed; index++) {
        state.addItem(currentPageId, {
          chartType: "table",
          x: last.x,
          y: nextY,
          w: last.w,
          h: NEW_PAGE_ITEM_ROWS,
          minW: last.minW,
          // minH 는 물려받지 않는다 — auto(한 표로 길게)였던 첫 아이템의 큰 minH 를 받으면 새 장이 그 높이로 묶인다
          minH: 6,
          dataSource: { ...last.dataSource },
          // 자동 높이는 아이템별 — 앞 장을 손으로 줄였어도 새 장은 내용에 맞춰 시작한다
          options: { ...last.options, autoSize: true },
        })
        nextY += NEW_PAGE_ITEM_ROWS
      }
      state.selectItem(selectedBefore)
    } else if (members.length > needed) {
      members.slice(needed).forEach((extra) => state.removeItem(currentPageId, extra.id))
    }
  }, [
    isManual,
    memberRank,
    groupId,
    isExporting,
    currentPageId,
    tableChunks.length,
    groupMembers.length,
    item,
  ])

  // 같은 내용 구성에서 이미 적용한 칸 높이들 (되풀이 방지용). 내용이 바뀌면 비운다.
  // 서명에 배치(y·페이지 경계 보정값)는 넣지 않는다 — 높이를 바꾸면 배치가 바뀌고 배치가 바뀌면 보정값이 바뀌어
  // 이력이 초기화되는 되먹임으로 두 높이 사이를 영원히 오갔다 (stage 에서 React #185, 2026-09-28)
  const appliedHeightsRef = useRef<Set<number>>(new Set())
  const appliedMinHRef = useRef<Set<number>>(new Set())
  const applyCountRef = useRef(0)
  // auto 는 페이지 여백이 위치(y)에 달려 있어 y 도 서명에 넣는다 — 되풀이는 서명마다 횟수 상한으로 막는다
  const contentSignature = `${bodyRows.length}|${headerColumns.length}|${displayColumnCount}|${sectionGap}|${availableWidth}|${isExporting}|${groupRank}|${isManual ? "" : item.y}`
  const lastSignatureRef = useRef(contentSignature)
  if (lastSignatureRef.current !== contentSignature) {
    lastSignatureRef.current = contentSignature
    appliedHeightsRef.current = new Set()
    appliedMinHRef.current = new Set()
    applyCountRef.current = 0
  }
  // 같은 내용에서 배치 갱신은 이 횟수까지만 — 어떤 경우에도 무한 반복하지 않는다
  const MAX_LAYOUT_APPLIES_PER_CONTENT = 8

  // 칸 높이 자동 조정 — 표 내용이 모두 들어가게 격자 칸을 키우고 줄인다 (내보내는 중에는 배치 변경 금지).
  // 사용자가 손으로 크기를 바꾼 아이템(autoSize false)은 건드리지 않는다
  // Table 은 항상 자동 높이 — 페이지 배치(auto)·장 배치(manual) 모두 높이가 계산값이다 (2026-09-29 요청: Auto Size 옵션 제거)
  const autoSize = true
  useEffect(() => {
    if (isExporting) {
      return
    }
    if (applyCountRef.current >= MAX_LAYOUT_APPLIES_PER_CONTENT) {
      return
    }
    if (!currentPageId || !tableRef.current) {
      return
    }
    if (bodyRows.length === 0 || headerColumns.length === 0) {
      return
    }
    // 머리글 높이가 통일된 뒤에 잰다 — 그 전 값으로 한 번, 그 뒤 값으로 또 한 번 바꾸면
    // 연속된 두 갱신이 GridLayout 의 props/state 를 어긋나게 해 두 높이 사이를 영원히 오간다(2026-09-23 재현)
    if (headerHeight === null) {
      return
    }

    // 배치가 끝난 뒤 한 번만 잰다 — 같은 프레임의 연속 실행은 마지막 것만 남는다
    let done = false
    const run = () => {
      if (done) {
        return
      }
      done = true
      const el = tableRef.current
      if (!el) {
        return
      }
      // 내용 높이는 마지막 덩어리의 아래 끝으로 잰다. scrollHeight 는 이미 커진 칸 높이를 되읽어
      // 페이지가 수십 장으로 불어나므로 쓰지 않는다 (일감 #151).
      const lastChunk = el.querySelector<HTMLElement>(".BuilderDataTable__chunk:last-of-type")
      if (!lastChunk) {
        return
      }

      const zoom = el.offsetWidth > 0 ? el.getBoundingClientRect().width / el.offsetWidth : 1
      const contentH = Math.ceil(
        (lastChunk.getBoundingClientRect().bottom - el.getBoundingClientRect().top) / (zoom || 1),
      )
      if (contentH <= 0) {
        return
      }

      // 격자 칸 높이와 표 영역 사이에 끼는 CanvasItem 크롬(테두리 4 + 안쪽 여백 16 + 머리말 30.4 ≈ 50px).
      // 표 묶음의 둘째 장부터는 머리말이 없다
      const CHROME_PX = isGrouped && groupRank > 0 ? 20 : 50
      // CanvasItem 위 여백(headGap)은 표 영역 밖이라 따로 더한다
      const appliedGap = usePdfBuilderStore.getState().headGaps[item.id] ?? 0
      const contentGridH = Math.max(4, Math.ceil((contentH + CHROME_PX + appliedGap) / 40))

      // 표는 행을 가리면서 내용보다 작게 줄일 수 없다 — 내용 높이를 최소 크기로 둔다 (2026-09-28 요청).
      // 손으로 크기를 바꾼 아이템(autoSize false)도 최소 크기는 지킨다
      const stored = usePdfBuilderStore
        .getState()
        .pages.find((page) => page.id === currentPageId)
        ?.items.find((pageItem) => pageItem.id === item.id)
      if (
        stored &&
        stored.minH !== contentGridH &&
        !appliedMinHRef.current.has(contentGridH) &&
        applyCountRef.current < MAX_LAYOUT_APPLIES_PER_CONTENT
      ) {
        appliedMinHRef.current.add(contentGridH)
        applyCountRef.current += 1
        updateItemLayout(currentPageId, item.id, {
          minH: contentGridH,
          ...(stored.h < contentGridH ? { h: contentGridH } : {}),
        })
      }
      if (!autoSize) {
        return
      }

      let gridH = contentGridH
      // 묶음의 마지막 장이 아니면 다음 장 아이템이 다음 페이지 첫머리에서 시작하도록 페이지 끝까지 채운다
      if (isGrouped && groupRank < tableChunks.length - 1) {
        // 내용이 실제로 시작하는 위치(경계 직전이면 다음 페이지 첫머리로 밀린 만큼 포함) 기준으로 페이지 끝을 잡는다
        gridH = Math.max(
          gridH,
          fillToPageBottomRows(item.y * GRID_ROW_PX + CANVAS_TOP_PX, groupedTopPad, pageHeight),
        )
      }

      // 같은 내용에서 이미 적용해 본 높이는 다시 적용하지 않는다 — 두 값 사이를 오가면
      // React 가 "Maximum update depth" 로 화면을 죽인다. 값마다 한 번으로 묶어 원천 차단한다.
      const currentH = usePdfBuilderStore
        .getState()
        .pages.find((page) => page.id === currentPageId)
        ?.items.find((pageItem) => pageItem.id === item.id)?.h
      if (
        gridH !== currentH &&
        !appliedHeightsRef.current.has(gridH) &&
        applyCountRef.current < MAX_LAYOUT_APPLIES_PER_CONTENT
      ) {
        appliedHeightsRef.current.add(gridH)
        applyCountRef.current += 1
        updateItemLayout(currentPageId, item.id, { h: gridH })
      }
    }
    // 창이 그려지지 않는 상태(숨긴 탭, 보이지 않는 창)에서는 requestAnimationFrame 이 멈추므로
    // 타이머를 함께 걸어 먼저 오는 쪽만 돈다 (2026-09-29)
    const frame = requestAnimationFrame(run)
    const timer = setTimeout(run, 100)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [
    isExporting,
    currentPageId,
    tableChunks,
    bodyRows.length,
    headerColumns.length,
    sectionGap,
    headerHeight,
    item.h,
    item.id,
    item.y,
    updateItemLayout,
    isGrouped,
    groupRank,
    pageHeight,
    groupedTopPad,
    autoSize,
  ])

  const hasData = renderedChunks.length > 0
  // 내보내는 중 데이터가 아직 안 왔으면 로딩 표식을 남겨 waitForDataReady 가 기다리게 한다
  const isDataStillLoading = hasActivitySelection && !!templateSection && isLoading && !hasData

  if (!hasData) {
    if (isExporting && !isDataStillLoading) {
      return null
    }
    const emptyMessage = !hasActivitySelection
      ? t("selectActivity", "Select an activity from Report Filters")
      : !templateSection
        ? t("tableNotInTemplate", "The selected report template has no Table section")
        : t("noData", "No data")

    return (
      <div
        className={`BuilderDataTable${isDataStillLoading ? " loading" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: colors.neutral.grey["600"],
          fontSize: 13,
        }}>
        {isExporting || isDataStillLoading ? "" : emptyMessage}
      </div>
    )
  }

  const splitClass = isSplitActive ? " BuilderDataTable--split" : ""

  // 덩어리 하나가 출력 한 장이 되므로, 편집 화면에서도 덩어리마다 정확히 한 장을 차지해야
  // 편집 화면의 장수와 실제 출력 장수가 같아진다. 첫 덩어리만 페이지의 남은 만큼만 차지한다.
  const GRID_ROW_H = 30
  const GRID_MARGIN_Y = 10
  const CONTAINER_PAD = 20
  const chunkTopPx = item.y * (GRID_ROW_H + GRID_MARGIN_Y) + CONTAINER_PAD + TITLE_BLOCK_PX
  const firstChunkSlotPx = pageHeight - (chunkTopPx % pageHeight)

  const pickColumns = (row: TableRow, columnIndexes: number[]): TableRow =>
    columnIndexes.map((index) => row[index] ?? null)

  return (
    <div ref={tableRef} className={`BuilderDataTable${splitClass}`}>
      {renderedChunks.map((chunk, idx) => (
        <div
          key={idx}
          className={`BuilderDataTable__chunk${idx > 0 && isExporting ? " BuilderDataTable__chunk--overflow" : ""}`}
          style={
            !isManual
              ? // auto: 표 하나가 페이지 한 장 — 위 여백(padTop)을 두고 상자 높이를 페이지 끝까지(slotPx) 고정한다.
                // 마지막 조각은 뒤에 이어질 것이 없으므로 내용만큼만 (빈 페이지가 하나 더 생기지 않게)
                {
                  boxSizing: "border-box",
                  ...(chunk.padTop > 0 ? { paddingTop: chunk.padTop } : {}),
                  ...(idx < renderedChunks.length - 1 && !isExporting
                    ? { minHeight: chunk.slotPx }
                    : {}),
                }
              : {
                  // margin 이 아니라 padding — 앞 덩어리의 margin-bottom(Section Gap)과 상쇄돼 사라진다
                  ...(idx > 0 && !isExporting ? { paddingTop: TITLE_BLOCK_PX } : {}),
                  // 묶음 장이 페이지 경계 직전에서 시작하면 다음 페이지 첫머리까지 띄운다
                  ...(isGrouped && groupedTopPad > 0 ? { paddingTop: groupedTopPad } : {}),
                  ...(idx < renderedChunks.length - 1 ? { marginBottom: sectionGap } : {}),
                  // (묶음이 아닌 옛 아이템) 덩어리 하나가 페이지 한 장을 차지하게 계산값으로 고정 — 재서 맞추면
                  // 되먹임으로 무한 반복한다. 마지막 덩어리는 뒤에 이어질 것이 없으므로 늘리지 않는다
                  ...(idx < renderedChunks.length - 1 && !isExporting
                    ? {
                        minHeight: Math.max(
                          0,
                          (idx === 0 ? firstChunkSlotPx : pageHeight) - sectionGap,
                        ),
                      }
                    : {}),
                }
          }>
          {chunk.label && <div className="BuilderDataTable__chunk-label">{chunk.label}</div>}
          {/* 리포트 PDF 표와 같은 마크업·스타일(printableTable.scss) */}
          <div className="renewal-pdf-container renewal-pdf table-container preview StatsTablePdfCont">
            <PrintableTableChunk
              tableHeader={chunk.columns}
              tableBody={[
                ...summaryRows.map((row) => pickColumns(row, chunk.columnIndexes)),
                ...chunk.rows.map((row) => pickColumns(row, chunk.columnIndexes)),
              ]}
              sort={sort}
              onHeaderClick={isExporting ? undefined : handleHeaderClick}
              hasSummaryRow={summaryRows.length > 0}
              // 소수점 자릿수 — 아이템 옵션(없으면 1자리, 정수 단위는 0) (2026-10-01 요청)
              currentDecimalPoint={item.options.decimalPlaces ?? 1}
              style={{ width: chunk.tableWidth, tableLayout: "fixed" }}
              headerRowStyle={headerHeight ? { height: headerHeight } : undefined}
              columnWidths={chunk.columnWidths}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
